import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import { AssignLevelForm } from './AssignLevelForm';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface LevelAssignmentModalsProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: create-level, create-assignment, deactivate-level and
 * revoke-assignment modals — relocated verbatim from the pre-rebuild
 * HierarchyPage.tsx (EAA-1812), unchanged behaviour. Levels sit below the
 * tree per this ticket's brief, but their CRUD forms are the same real
 * mutations as before.
 */
export function LevelAssignmentModals({ vm }: LevelAssignmentModalsProps) {
    const { t } = useTranslation();
    const {
        levelModalOpen,
        setLevelModalOpen,
        levelForm,
        setLevelForm,
        levelError,
        onCreateLevel,
        createLevel,
        assignModalOpen,
        setAssignModalOpen,
        assignForm,
        setAssignForm,
        assignError,
        onCreateAssignment,
        createAssignment,
        activeLevels,
        confirmDeactivateLevel,
        setConfirmDeactivateLevel,
        deactivateLevel,
        confirmRevokeAssignment,
        setConfirmRevokeAssignment,
        revokeAssignment,
    } = vm;

    function closeLevelModal() {
        setLevelModalOpen(false);
    }
    function closeAssignModal() {
        setAssignModalOpen(false);
    }

    return (
        <>
            <Modal
                open={levelModalOpen}
                onCancel={closeLevelModal}
                title={t('console.hierarchy.levelAssignmentModals.createLevelTitle')}
                footer={
                    <>
                        <button type="button" className="portal-btn" onClick={closeLevelModal}>
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            form="hierarchy-create-level-form"
                            className="portal-btn portal-btn-primary"
                            disabled={createLevel.isPending}
                        >
                            {createLevel.isPending
                                ? t('console.hierarchy.levelAssignmentModals.creatingLabel')
                                : t('console.hierarchy.levelAssignmentModals.createLevelTitle')}
                        </button>
                    </>
                }
            >
                <form
                    id="hierarchy-create-level-form"
                    className="portal-admin-form"
                    onSubmit={onCreateLevel}
                >
                    <input
                        type="text"
                        placeholder={t(
                            'console.hierarchy.levelAssignmentModals.namePlaceholder'
                        )}
                        value={levelForm.name}
                        onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                    />
                    <input
                        type="text"
                        placeholder={t(
                            'console.hierarchy.levelAssignmentModals.roleKeyPlaceholder'
                        )}
                        value={levelForm.role_key}
                        onChange={(e) => setLevelForm({ ...levelForm, role_key: e.target.value })}
                    />
                    <input
                        type="text"
                        placeholder={t(
                            'console.hierarchy.levelAssignmentModals.descriptionPlaceholder'
                        )}
                        value={levelForm.description}
                        onChange={(e) =>
                            setLevelForm({ ...levelForm, description: e.target.value })
                        }
                    />
                    {levelError && <p className="portal-admin-error">{levelError}</p>}
                </form>
            </Modal>

            <Modal
                open={assignModalOpen}
                onCancel={closeAssignModal}
                title={t('console.hierarchy.levelAssignmentModals.assignTitle')}
                footer={
                    <>
                        <button type="button" className="portal-btn" onClick={closeAssignModal}>
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            form="hierarchy-create-assignment-form"
                            className="portal-btn portal-btn-primary"
                            disabled={createAssignment.isPending || activeLevels.length === 0}
                        >
                            {createAssignment.isPending
                                ? t('console.hierarchy.levelAssignmentModals.assigningLabel')
                                : t('console.hierarchy.levelAssignmentModals.assignSubmit')}
                        </button>
                    </>
                }
            >
                {/* EAA-1839: the three fields moved to the shared `AssignLevelForm`
                    — `/console/hierarchy/accounts`'s "+ Tài khoản" writes the very
                    same endpoint with the very same body, and two copies of one
                    request shape drift. State stays here (this modal's own
                    `assignForm`/`assignError`/`createAssignment` are untouched);
                    only the field markup is shared. */}
                <AssignLevelForm
                    formId="hierarchy-create-assignment-form"
                    value={assignForm}
                    onChange={setAssignForm}
                    levels={activeLevels}
                    error={assignError}
                    onSubmit={onCreateAssignment}
                />
            </Modal>

            <Modal
                open={Boolean(confirmDeactivateLevel)}
                onCancel={() => setConfirmDeactivateLevel(null)}
                title={t('console.hierarchy.levelAssignmentModals.deactivateLevelTitle')}
                footer={
                    <>
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={() => setConfirmDeactivateLevel(null)}
                            disabled={deactivateLevel.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-danger"
                            disabled={deactivateLevel.isPending}
                            onClick={() => {
                                if (confirmDeactivateLevel)
                                    deactivateLevel.mutate(confirmDeactivateLevel.id);
                            }}
                        >
                            {deactivateLevel.isPending
                                ? t('console.hierarchy.processingLabel')
                                : t('console.hierarchy.levelAssignmentModals.deactivateLevelConfirm')}
                        </button>
                    </>
                }
            >
                <p className="portal-confirm-message">
                    {t('console.hierarchy.levelAssignmentModals.deactivateLevelMessage', {
                        name: confirmDeactivateLevel?.name,
                    })}
                </p>
            </Modal>

            <Modal
                open={Boolean(confirmRevokeAssignment)}
                onCancel={() => setConfirmRevokeAssignment(null)}
                title={t('console.hierarchy.levelAssignmentModals.revokeAssignmentTitle')}
                footer={
                    <>
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={() => setConfirmRevokeAssignment(null)}
                            disabled={revokeAssignment.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-danger"
                            disabled={revokeAssignment.isPending}
                            onClick={() => {
                                if (confirmRevokeAssignment)
                                    revokeAssignment.mutate(confirmRevokeAssignment.id);
                            }}
                        >
                            {revokeAssignment.isPending
                                ? t('console.hierarchy.processingLabel')
                                : t('console.hierarchy.levelAssignmentModals.revokeButton')}
                        </button>
                    </>
                }
            >
                <p className="portal-confirm-message">
                    {t('console.hierarchy.levelAssignmentModals.revokeMessage', {
                        identity:
                            confirmRevokeAssignment?.user_email ||
                            confirmRevokeAssignment?.user_sub,
                    })}
                </p>
            </Modal>
        </>
    );
}

export default LevelAssignmentModals;
